import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../services/axios';
import DetalleBoleta from '../components/DetalleBoleta';
import Modal from '../components/Modal';
import { formatApiDate } from '../utils/date';

function DashboardCliente() {
  const { user, logout, API_URL } = useAuth();
  const navigate = useNavigate();
  const [boletas, setBoletas] = useState([]);
  const [lecturas, setLecturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    consumoActual: 0,
    totalAPagar: 0,
    boletasPendientes: 0,
  });
  const [boletaSeleccionada, setBoletaSeleccionada] = useState(null);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [descargandoId, setDescargandoId] = useState(null);
  const [descargandoFacturaId, setDescargandoFacturaId] = useState(null);
  const [planConfig, setPlanConfig] = useState({ interesPorcentaje: 0, maxCuotas: 5 });
  const [planModal, setPlanModal] = useState({ isOpen: false, boleta: null });
  const [planConsolidadoModal, setPlanConsolidadoModal] = useState(false);
  const [showDetallePlan, setShowDetallePlan] = useState(false);
  const [planActivo, setPlanActivo] = useState(null);
  const [planCuotas, setPlanCuotas] = useState(2);
  const [planLoading, setPlanLoading] = useState(false);
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  useEffect(() => {
    cargarDatos();
    cargarConfigPlanPago();
    verificarPlanActivo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarDatos = async () => {
    try {
      // Cargar boletas con relaciones completas
      const boletasRes = await axios.get(`${API_URL}/boletas/usuario/${user.id}`);
      const boletasOrdenadas = boletasRes.data.sort((a, b) => {
        if (a.anio !== b.anio) return b.anio - a.anio;
        return b.mes - a.mes;
      });
      setBoletas(boletasOrdenadas);

      // Calcular total de boletas pendientes
      const boletasPendientes = boletasOrdenadas.filter(b =>
        ['pendiente', 'procesando', 'vencida'].includes(b.estado)
      );
      const totalPendiente = boletasPendientes
        .reduce((sum, b) => sum + parseFloat(b.total || 0), 0);

      // Construir historial de consumo a partir de boletas (los clientes no pueden consultar /lecturas)
      const lecturasDesdeBoletas = boletasOrdenadas.map(b => {
        const consumo = parseFloat(
          b.consumo_m3 ?? b?.lectura?.consumoM3 ?? 0
        ) || 0;
        return {
          mes: b.mes,
          anio: b.anio,
          consumoM3: consumo,
        };
      });

      // Ordenar cronológicamente y tomar las 6 últimas para mostrar izquierda→derecha
      const lecturasOrdenadasAsc = lecturasDesdeBoletas.sort((a, b) => {
        if (a.anio !== b.anio) return a.anio - b.anio;
        return a.mes - b.mes;
      });
      const lecturasParaGrafico = lecturasOrdenadasAsc.slice(-6);
      setLecturas(lecturasParaGrafico);

      // Usar el consumo del período más reciente disponible
      const consumoActual = lecturasParaGrafico.length > 0
        ? lecturasParaGrafico[lecturasParaGrafico.length - 1].consumoM3
        : 0;

      // Actualizar stats
      setStats({
        consumoActual: consumoActual,
        totalAPagar: totalPendiente,
        boletasPendientes: boletasPendientes.length,
      });
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarConfigPlanPago = async () => {
    try {
      const response = await axios.get('/tarifario/plan-pago/config');
      setPlanConfig({
        interesPorcentaje: Number(response.data?.interesPorcentaje || 0),
        maxCuotas: Number(response.data?.maxCuotas || 5),
      });
    } catch (error) {
      console.error('Error al cargar configuración de plan de pago:', error);
    }
  };

  const verificarPlanActivo = async () => {
    try {
      const response = await axios.get(`/tarifario/plan-pago/activo/${user.id}`);
      if (response.data?.tienePlanActivo) {
        setPlanActivo(response.data.planDetalles);
      } else {
        setPlanActivo(null);
      }
    } catch (error) {
      console.error('Error al verificar plan activo:', error);
      setPlanActivo(null);
    }
  };

  const abrirPlanPagoConsolidado = () => {
    const maxCuotas = Number(planConfig.maxCuotas || 1);
    const cuotasInicial = Math.min(maxCuotas, maxCuotas >= 2 ? 2 : 1);
    setPlanCuotas(cuotasInicial);
    setPlanConsolidadoModal(true);
  };

  const cerrarPlanPagoConsolidado = () => {
    setPlanConsolidadoModal(false);
  };

  const confirmarPlanPagoConsolidado = async () => {
    setPlanLoading(true);
    try {
      const response = await axios.post('/tarifario/plan-pago-consolidado', {
        usuarioId: user.id,
        cantidadCuotas: Number(planCuotas),
      });

      setModal({
        isOpen: true,
        type: 'success',
        title: 'Plan de pago consolidado creado',
        message: `Se creó un plan de pago para ${response.data.plan.cantidadBoletas} boleta(s). Las cuotas se cargarán en las próximas boletas.`,
      });

      setPlanConsolidadoModal(false);
      await cargarDatos();
      await verificarPlanActivo();
    } catch (error) {
      console.error('Error al crear plan de pago consolidado:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'No se pudo crear el plan',
        message: error.response?.data?.message || 'Intenta nuevamente en unos minutos.',
      });
    } finally {
      setPlanLoading(false);
    }
  };

  const handleVerDetalle = async (boleta) => {
    try {
      // Cargar boleta completa con todas las relaciones
      const response = await axios.get(`${API_URL}/boletas/${boleta.id}`);
      setBoletaSeleccionada(response.data);
      setShowDetalleModal(true);
    } catch (error) {
      console.error('Error al cargar detalle de boleta:', error);
    }
  };

  const handleCerrarDetalle = () => {
    setShowDetalleModal(false);
    setBoletaSeleccionada(null);
  };

  const handlePagarBoleta = (boleta) => {
    navigate(`/cliente/pagar-boleta/${boleta.id}`);
  };

  const handleDescargarPdf = async (boleta) => {
    if (!boleta?.id) return;
    setDescargandoId(boleta.id);
    try {
      const response = await axios.get(`/tarifario/pdf/boleta/${boleta.id}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const padron = user?.padron || boleta?.usuario?.padron || 'cliente';
      const mes = boleta?.mes || 'mes';
      const anio = boleta?.anio || 'anio';
      link.href = url;
      link.setAttribute('download', `boleta-${padron}-${mes}-${anio}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar PDF:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'No se pudo descargar el PDF',
        message: error.response?.data?.message || 'Intenta nuevamente en unos minutos.',
      });
    } finally {
      setDescargandoId(null);
    }
  };

  const handleDescargarFacturaPdf = async (boleta) => {
    if (!boleta?.id) return;
    setDescargandoFacturaId(boleta.id);
    try {
      const response = await axios.get(`/tarifario/pdf/factura/${boleta.id}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const padron = user?.padron || boleta?.usuario?.padron || 'cliente';
      const mes = boleta?.mes || 'mes';
      const anio = boleta?.anio || 'anio';
      link.href = url;
      link.setAttribute('download', `factura-${padron}-${mes}-${anio}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar factura:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'No se pudo descargar la factura',
        message: error.response?.data?.message || 'La factura aún no está emitida o hubo un error.',
      });
    } finally {
      setDescargandoFacturaId(null);
    }
  };

  const abrirPlanPago = (boleta) => {
    if (!boleta) return;
    const maxCuotas = Number(planConfig.maxCuotas || 1);
    const cuotasInicial = Math.min(maxCuotas, maxCuotas >= 2 ? 2 : 1);
    setPlanCuotas(cuotasInicial);
    setPlanModal({ isOpen: true, boleta });
  };

  const cerrarPlanPago = () => {
    setPlanModal({ isOpen: false, boleta: null });
  };

  const confirmarPlanPago = async () => {
    if (!planModal.boleta) return;
    setPlanLoading(true);
    try {
      const response = await axios.post('/tarifario/plan-pago', {
        boletaId: planModal.boleta.id,
        cantidadCuotas: Number(planCuotas),
      });

      setModal({
        isOpen: true,
        type: 'success',
        title: 'Plan de pago creado',
        message: 'Tu plan de pago fue registrado correctamente. Las cuotas se cargarán en las próximas boletas.',
      });

      setPlanModal({ isOpen: false, boleta: null });
      if (response.data?.boleta) {
        setBoletaSeleccionada(response.data.boleta);
      }
      await cargarDatos();
    } catch (error) {
      console.error('Error al crear plan de pago:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'No se pudo crear el plan',
        message: error.response?.data?.message || 'Intenta nuevamente en unos minutos.',
      });
    } finally {
      setPlanLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const cerrarModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }));
  };

  const estadoColor = (estado) => {
    switch (estado) {
      case 'pagada':
        return 'bg-green-500/10 text-green-600';
      case 'procesando':
        return 'bg-blue-500/10 text-blue-600';
      case 'pendiente':
        return 'bg-yellow-500/10 text-yellow-600';
      case 'vencida':
        return 'bg-red-500/10 text-red-600';
      case 'plan_pago':
        return 'bg-indigo-500/10 text-indigo-600';
      default:
        return 'bg-gray-500/10 text-gray-600';
    }
  };

  const estadoTexto = (estado) => {
    switch (estado) {
      case 'pagada':
        return 'Pagada';
      case 'procesando':
        return 'En Proceso';
      case 'pendiente':
        return 'Pendiente';
      case 'vencida':
        return 'Vencida';
      case 'plan_pago':
        return 'Plan de pago';
      default:
        return estado;
    }
  };

  const formatearMes = (mes) => {
    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return meses[mes - 1] || mes;
  };

  const formatearPeriodo = (mes, anio) => {
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${meses[mes - 1]} ${anio}`;
  };

  const formatearFecha = (fecha) => {
    return formatApiDate(fecha, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatearFechaHora = (fecha) => {
    return new Date(fecha).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatearMonto = (monto) => {
    const numero = Number(monto) || 0;
    return numero.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const calcularCuotasPlan = (montoTotal, cantidadCuotas) => {
    if (!cantidadCuotas || cantidadCuotas < 1) return [];
    const base = Number((montoTotal / cantidadCuotas).toFixed(2));
    const cuotas = Array.from({ length: cantidadCuotas }, () => base);
    const totalBase = Number((base * cantidadCuotas).toFixed(2));
    const ajuste = Number((montoTotal - totalBase).toFixed(2));
    cuotas[cuotas.length - 1] = Number((cuotas[cuotas.length - 1] + ajuste).toFixed(2));
    return cuotas;
  };

  const obtenerInicioPlanTexto = () => {
    const ahora = new Date();
    let mes = ahora.getMonth() + 2;
    let anio = ahora.getFullYear();
    if (mes > 12) {
      mes = 1;
      anio += 1;
    }
    return `${String(mes).padStart(2, '0')}/${anio}`;
  };

  const calcularAltura = (consumo) => {
    if (!lecturas.length) return 0;
    const consumos = lecturas.map(l => Number(l.consumoM3) || 0);
    const maxConsumo = Math.max(...consumos);
    if (!Number.isFinite(maxConsumo) || maxConsumo <= 0) return 0;
    const valor = Number(consumo) || 0;
    const altura = (valor / maxConsumo) * 100;
    // Asegurar que al menos se vea algo si hay consumo
    return altura > 0 ? Math.max(altura, 10) : 0;
  };

  const ultimoPagoRechazado = useMemo(() => {
    const pagosRechazados = boletas.flatMap(b =>
      (b.pagos || []).filter(p => p.estado === 'rechazado').map(p => ({ ...p, boleta: b }))
    );
    if (!pagosRechazados.length) return null;
    return pagosRechazados.sort((a, b) => new Date(b.fechaPago) - new Date(a.fechaPago))[0];
  }, [boletas]);

  const boletasPendientes = useMemo(() => (
    boletas.filter(b => ['pendiente', 'procesando', 'vencida'].includes(b.estado))
  ), [boletas]);

  const boletaMasReciente = useMemo(() => (
    boletas.length ? boletas[0] : null
  ), [boletas]);

  const proximaBoleta = useMemo(() => {
    if (!boletasPendientes.length) return null;
    return [...boletasPendientes].sort(
      (a, b) => new Date(a.fechaVencimiento) - new Date(b.fechaVencimiento)
    )[0];
  }, [boletasPendientes]);

  const ultimaBoletaPagada = useMemo(() => {
    const pagadas = boletas.filter(b => b.estado === 'pagada' && (b.fechaPago || b.fechaEmision));
    if (!pagadas.length) return null;
    return [...pagadas].sort(
      (a, b) => new Date(b.fechaPago || b.fechaEmision) - new Date(a.fechaPago || a.fechaEmision)
    )[0];
  }, [boletas]);

  const consumoPromedio = useMemo(() => {
    if (!lecturas.length) return 0;
    const total = lecturas.reduce((sum, l) => sum + (l.consumoM3 || 0), 0);
    return total / lecturas.length;
  }, [lecturas]);

  const planBoletaTotal = Number(planModal.boleta?.total || 0);
  const planInteresPorcentaje = Number(planConfig.interesPorcentaje || 0);
  const planMontoInteres = Number(((planBoletaTotal * planInteresPorcentaje) / 100).toFixed(2));
  const planMontoTotal = Number((planBoletaTotal + planMontoInteres).toFixed(2));
  const cuotasPlan = calcularCuotasPlan(planMontoTotal, Number(planCuotas) || 0);
  const planMontoCuota = cuotasPlan[0] || 0;
  const planMontoCuotaUltima = cuotasPlan[cuotasPlan.length - 1] || 0;
  const planInicioTexto = obtenerInicioPlanTexto();
  const maxCuotasPlan = Math.max(1, Number(planConfig.maxCuotas || 1));

  // Variables para plan consolidado
  const boletasAdeudadas = useMemo(() => (
    boletas.filter(b => ['pendiente', 'vencida'].includes(b.estado))
  ), [boletas]);
  const totalDeudaConsolidado = useMemo(() => (
    boletasAdeudadas.reduce((sum, b) => sum + parseFloat(b.total || 0), 0)
  ), [boletasAdeudadas]);
  const consolidadoInteres = Number(((totalDeudaConsolidado * planInteresPorcentaje) / 100).toFixed(2));
  const consolidadoMontoTotal = Number((totalDeudaConsolidado + consolidadoInteres).toFixed(2));
  const consolidadoCuotas = calcularCuotasPlan(consolidadoMontoTotal, Number(planCuotas) || 0);
  const consolidadoMontoCuota = consolidadoCuotas[0] || 0;
  const consolidadoMontoCuotaUltima = consolidadoCuotas[consolidadoCuotas.length - 1] || 0;


  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f7f8] dark:bg-[#101c22] flex items-center justify-center">
        <div className="text-lg text-gray-600 dark:text-gray-400">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f7f8] dark:bg-[#101c22]">
      {/* Modal de Detalle */}
      {showDetalleModal && (
        <DetalleBoleta
          boleta={boletaSeleccionada}
          onClose={handleCerrarDetalle}
          onDescargarPdf={handleDescargarPdf}
          descargandoPdf={descargandoId === boletaSeleccionada?.id}
          onDescargarFacturaPdf={handleDescargarFacturaPdf}
          descargandoFacturaPdf={descargandoFacturaId === boletaSeleccionada?.id}

        />
      )}

      {/* Modal Detalle Plan Activo */}
      {planActivo && showDetallePlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#101c22] rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-[#111618] dark:text-white">
                Detalle de Plan Activo
              </h3>
              <button
                onClick={() => setShowDetallePlan(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total financiado</p>
                  <p className="text-xl font-bold text-[#111618] dark:text-white">
                    ${formatearMonto(planActivo.montoTotal)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Cuotas</p>
                  <p className="font-semibold text-[#111618] dark:text-white">
                    {planActivo.cuotas} x ${formatearMonto(planActivo.montoCuota)}
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Inicio del plan</p>
                    <p className="font-medium text-[#111618] dark:text-white">
                      {String(planActivo.inicioMes).padStart(2, '0')}/{planActivo.inicioAnio}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Interés aplicado</p>
                    <p className="font-medium text-[#111618] dark:text-white">
                      {planActivo.interesPorcentaje}%
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Boletas agrupadas</p>
                    <p className="font-medium text-[#111618] dark:text-white">
                      {planActivo.cantidadBoletas}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Código Grupo</p>
                    <p className="font-medium text-[#111618] dark:text-white truncate" title={planActivo.grupoId}>
                      {planActivo.grupoId?.substring(0, 8)}...
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <button
                  onClick={() => setShowDetallePlan(false)}
                  className="w-full py-3 bg-gray-100 dark:bg-gray-700 text-[#111618] dark:text-white font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {planModal.isOpen && planModal.boleta && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#101c22] rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-[#111618] dark:text-white">
                Crear plan de pago
              </h3>
              <button
                onClick={cerrarPlanPago}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="space-y-4 text-sm text-[#111618] dark:text-gray-200">
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Boleta</p>
                <p className="font-semibold">
                  {formatearPeriodo(planModal.boleta.mes, planModal.boleta.anio)}
                </p>
                <p className="text-gray-600 dark:text-gray-400">
                  Monto actual: ${formatearMonto(planBoletaTotal)}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Interés</p>
                  <p className="font-semibold">{planInteresPorcentaje}%</p>
                  <p className="text-gray-600 dark:text-gray-400">
                    +${formatearMonto(planMontoInteres)}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Total plan</p>
                  <p className="font-semibold">${formatearMonto(planMontoTotal)}</p>
                  <p className="text-gray-600 dark:text-gray-400">
                    Inicio: {planInicioTexto}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cantidad de cuotas (máx. {maxCuotasPlan})
                </label>
                <select
                  value={planCuotas}
                  onChange={(e) => setPlanCuotas(Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-[#0f1a21] dark:border-gray-700"
                >
                  {Array.from({ length: maxCuotasPlan }, (_, i) => i + 1).map((cuota) => (
                    <option key={cuota} value={cuota}>
                      {cuota} {cuota === 1 ? 'cuota' : 'cuotas'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-lg border border-indigo-200 bg-indigo-50 dark:border-indigo-900/30 dark:bg-indigo-900/20 p-4">
                <p className="text-sm text-indigo-900 dark:text-indigo-100">
                  Cuota estimada: ${formatearMonto(planMontoCuota)}
                  {planMontoCuotaUltima !== planMontoCuota
                    ? ` (última ${formatearMonto(planMontoCuotaUltima)})`
                    : ''}
                </p>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={cerrarPlanPago}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={planLoading}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarPlanPago}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={planLoading}
              >
                {planLoading ? 'Creando...' : 'Confirmar plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Plan Consolidado */}
      {planConsolidadoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#101c22] rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-[#111618] dark:text-white">
                Crear Plan de Pago Consolidado
              </h3>
              <button
                onClick={cerrarPlanPagoConsolidado}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="space-y-4 text-sm text-[#111618] dark:text-gray-200">
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 dark:border-indigo-900/30 dark:bg-indigo-900/20 p-4">
                <p className="text-xs uppercase text-indigo-600 dark:text-indigo-300">Total de Boletas</p>
                <p className="font-semibold text-lg">
                  {boletasAdeudadas.length} boleta{boletasAdeudadas.length > 1 ? 's' : ''}
                </p>
                <p className="text-xs text-indigo-600 dark:text-indigo-300 mt-1">
                  {boletasAdeudadas.map(b => `${String(b.mes).padStart(2, '0')}/${b.anio}`).join(', ')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Deuda Total</p>
                  <p className="font-semibold">${formatearMonto(totalDeudaConsolidado)}</p>
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Interés {planInteresPorcentaje}%</p>
                  <p className="font-semibold">+${formatearMonto(consolidadoInteres)}</p>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Total del Plan</p>
                <p className="font-semibold text-lg">${formatearMonto(consolidadoMontoTotal)}</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Inicio: {planInicioTexto}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cantidad de cuotas (máx. {maxCuotasPlan})
                </label>
                <select
                  value={planCuotas}
                  onChange={(e) => setPlanCuotas(Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-[#0f1a21] dark:border-gray-700"
                >
                  {Array.from({ length: maxCuotasPlan }, (_, i) => i + 1).map((cuota) => (
                    <option key={cuota} value={cuota}>
                      {cuota} {cuota === 1 ? 'cuota' : 'cuotas'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-lg border border-indigo-200 bg-indigo-50 dark:border-indigo-900/30 dark:bg-indigo-900/20 p-4">
                <p className="text-sm text-indigo-900 dark:text-indigo-100">
                  Cuota estimada: ${formatearMonto(consolidadoMontoCuota)}
                  {consolidadoMontoCuotaUltima !== consolidadoMontoCuota
                    ? ` (última ${formatearMonto(consolidadoMontoCuotaUltima)})`
                    : ''}
                </p>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={cerrarPlanPagoConsolidado}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={planLoading}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarPlanPagoConsolidado}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={planLoading}
              >
                {planLoading ? 'Creando...' : 'Confirmar Plan Consolidado'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={modal.isOpen}
        onClose={cerrarModal}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onConfirm={cerrarModal}
      />

      {/* Header */}
      <header className="bg-white dark:bg-[#101c22] border-b border-gray-100 dark:border-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#007BFF] flex items-center justify-center text-white font-bold text-base shadow-sm flex-shrink-0">
                {user?.nombre?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div>
                <p className="text-xs text-[#617c89] dark:text-gray-400 leading-none mb-0.5">Portal Cliente</p>
                <p className="text-sm font-semibold text-[#111618] dark:text-white leading-none">
                  {user?.nombre} <span className="text-[#617c89] dark:text-gray-400 font-normal">· Padrón {user?.padron}</span>
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-[#617c89] dark:text-gray-400 hover:text-[#111618] dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {ultimoPagoRechazado && ultimoPagoRechazado.boleta?.estado === 'pendiente' && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <div className="flex items-start gap-2">
              <span className="material-symbols-outlined">cancel</span>
              <div>
                <p className="font-semibold">Pago rechazado</p>
                <p className="mt-1">
                  Boleta {ultimoPagoRechazado.boleta.mes}/{ultimoPagoRechazado.boleta.anio} • ID #{ultimoPagoRechazado.boleta.id}
                </p>
                {ultimoPagoRechazado.observaciones && (
                  <p className="mt-1">{ultimoPagoRechazado.observaciones}</p>
                )}
                <p className="mt-1 text-xs text-red-700">
                  Fecha: {formatearFechaHora(ultimoPagoRechazado.fechaPago)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Page Heading */}
        <div className="mb-6">
          <h1 className="text-[#111618] dark:text-white text-2xl md:text-3xl font-bold leading-tight">
            Bienvenido, <span className="text-[#007BFF]">{user?.nombre}</span>
          </h1>
          <p className="text-sm text-[#617c89] dark:text-gray-400 mt-1">Resumen de tu cuenta de servicio de agua</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Consumo actual */}
          <div className="flex items-center gap-4 rounded-xl p-5 bg-white dark:bg-[#101c22] border border-[#dbe2e6] dark:border-gray-700 border-l-4 border-l-[#007BFF]">
            <div className="w-10 h-10 rounded-lg bg-[#007BFF]/10 dark:bg-[#007BFF]/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-[#007BFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-[#617c89] dark:text-gray-400 font-medium uppercase tracking-wide">
                Consumo actual
              </p>
              <p className="text-2xl font-bold text-[#111618] dark:text-white mt-0.5">
                {stats.consumoActual.toFixed(0)}<span className="text-sm font-normal text-gray-400 ml-1">m³</span>
              </p>
            </div>
          </div>

          {/* Promedio */}
          <div className="flex items-center gap-4 rounded-xl p-5 bg-white dark:bg-[#101c22] border border-[#dbe2e6] dark:border-gray-700 border-l-4 border-l-sky-400">
            <div className="w-10 h-10 rounded-lg bg-sky-50 dark:bg-sky-900/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-[#617c89] dark:text-gray-400 font-medium uppercase tracking-wide">
                Promedio histórico
              </p>
              <p className="text-2xl font-bold text-[#111618] dark:text-white mt-0.5">
                {consumoPromedio.toFixed(0)}<span className="text-sm font-normal text-gray-400 ml-1">m³</span>
              </p>
            </div>
          </div>

          {/* Boletas pendientes */}
          <div className={`flex items-center gap-4 rounded-xl p-5 bg-white dark:bg-[#101c22] border border-[#dbe2e6] dark:border-gray-700 ${
            stats.boletasPendientes > 0 ? 'border-l-4 border-l-amber-400' : 'border-l-4 border-l-green-400'
          }`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              stats.boletasPendientes > 0 ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-green-50 dark:bg-green-900/20'
            }`}>
              <svg className={`w-5 h-5 ${stats.boletasPendientes > 0 ? 'text-amber-500' : 'text-green-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-[#617c89] dark:text-gray-400 font-medium uppercase tracking-wide">
                Boletas pendientes
              </p>
              <p className={`text-2xl font-bold mt-0.5 ${stats.boletasPendientes > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                {stats.boletasPendientes}
              </p>
            </div>
          </div>

          {/* Total a pagar */}
          <div className={`flex items-center gap-4 rounded-xl p-5 bg-white dark:bg-[#101c22] border border-[#dbe2e6] dark:border-gray-700 ${
            stats.totalAPagar > 0 ? 'border-l-4 border-l-red-400' : 'border-l-4 border-l-green-400'
          }`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              stats.totalAPagar > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20'
            }`}>
              <svg className={`w-5 h-5 ${stats.totalAPagar > 0 ? 'text-red-500' : 'text-green-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-[#617c89] dark:text-gray-400 font-medium uppercase tracking-wide">
                Total a pagar
              </p>
              <p className={`text-2xl font-bold mt-0.5 ${stats.totalAPagar > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                ${parseFloat(stats.totalAPagar).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Grid: Historial (2/3) + Sidebar (1/3) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">

          {/* Historial de Consumo — 2 columnas */}
          <div className="lg:col-span-2 rounded-xl border border-[#dbe2e6] dark:border-gray-700 bg-white dark:bg-[#101c22] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-base font-semibold text-[#111618] dark:text-white">Historial de consumo</h2>
              <div className="flex items-center gap-3">
                <p className="text-[#007BFF] text-xl font-bold">
                  {lecturas.reduce((sum, l) => sum + (l.consumoM3 || 0), 0).toFixed(0)}
                  <span className="text-sm font-normal text-gray-400 ml-1">m³</span>
                </p>
                <span className="text-xs text-[#617c89] dark:text-gray-400">
                  Últimos {lecturas.length} meses
                </span>
              </div>
            </div>
            <div className="p-6">
              {lecturas.length > 0 ? (
                <div className="flex items-end justify-around gap-2 h-[200px] px-2 pb-2">
                  {lecturas.map((lectura, index) => {
                    const altura = calcularAltura(lectura.consumoM3);
                    const consumo = parseFloat(lectura.consumoM3) || 0;
                    return (
                      <div key={index} className="flex flex-col items-center flex-1 h-full justify-end group relative">
                        <div className="absolute bottom-full mb-2 hidden group-hover:flex items-center bg-gray-900 text-white text-xs rounded-lg py-1 px-2 whitespace-nowrap z-10 shadow-lg">
                          {consumo.toFixed(1)} m³
                        </div>
                        <div
                          className={`${index === lecturas.length - 1
                            ? 'bg-[#007BFF]'
                            : 'bg-[#007BFF]/20 dark:bg-[#007BFF]/25 group-hover:bg-[#007BFF]/60'
                          } w-full rounded-t-md transition-all duration-200`}
                          style={{
                            height: altura > 0 ? `${altura}%` : '5%',
                            minHeight: consumo > 0 ? '16px' : '4px'
                          }}
                        />
                        <p className={`${index === lecturas.length - 1
                          ? 'text-[#007BFF] font-semibold'
                          : 'text-[#617c89] dark:text-gray-500'
                        } text-xs pt-2 whitespace-nowrap`}>
                          {formatearMes(lectura.mes)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-[200px] flex flex-col items-center justify-center text-[#617c89] dark:text-gray-400">
                  <svg className="w-12 h-12 mb-3 text-gray-200 dark:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p className="text-sm">Sin datos de consumo aún</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar derecha: Resumen + Plan apilados */}
          <div className="flex flex-col gap-4">

            {/* Resumen */}
            <div className="rounded-xl border border-[#dbe2e6] dark:border-gray-700 bg-white dark:bg-[#101c22] overflow-hidden flex-1">
              <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
                <h2 className="text-base font-semibold text-[#111618] dark:text-white">Próximo vencimiento</h2>
              </div>
              <div className="p-5 space-y-4">
                {proximaBoleta ? (
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-[#111618] dark:text-white">
                          {formatearPeriodo(proximaBoleta.mes, proximaBoleta.anio)}
                        </p>
                        <p className="text-xs text-[#617c89] dark:text-gray-400 mt-0.5">
                          Vence el {formatearFecha(proximaBoleta.fechaVencimiento)}
                        </p>
                      </div>
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold flex-shrink-0 ${estadoColor(proximaBoleta.estado)}`}>
                        {estadoTexto(proximaBoleta.estado)}
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-[#111618] dark:text-white mt-3">
                      ${parseFloat(proximaBoleta.total).toFixed(2)}
                    </p>
                    <button
                      onClick={() => navigate(`/cliente/pagar-boleta/${proximaBoleta.id}`)}
                      className="mt-3 w-full py-2.5 bg-[#007BFF] text-white text-sm font-semibold rounded-lg hover:bg-[#007BFF]/90 transition-colors"
                    >
                      Pagar ahora
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 py-2">
                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-green-700 dark:text-green-400">¡Al día!</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Sin boletas pendientes</p>
                    </div>
                  </div>
                )}

                {boletaMasReciente && (
                  <div className="pt-3 border-t border-gray-100 dark:border-gray-700/60">
                    <p className="text-xs text-[#617c89] dark:text-gray-400 mb-1">Última boleta emitida</p>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-[#111618] dark:text-gray-200">
                        {formatearPeriodo(boletaMasReciente.mes, boletaMasReciente.anio)}
                      </p>
                      <button
                        onClick={() => handleDescargarPdf(boletaMasReciente)}
                        disabled={descargandoId === boletaMasReciente?.id}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                      >
                        <span className="material-symbols-outlined text-sm">picture_as_pdf</span>
                        PDF
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Plan de Pago */}
            <div className="rounded-xl border border-[#dbe2e6] dark:border-gray-700 bg-white dark:bg-[#101c22] overflow-hidden">
              <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
                <h2 className="text-base font-semibold text-[#111618] dark:text-white">Plan de pago</h2>
              </div>
              <div className="p-5">
                {planActivo ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#617c89] dark:text-gray-400">
                        {planActivo.cantidadBoletas} boleta{planActivo.cantidadBoletas > 1 ? 's' : ''} agrupada{planActivo.cantidadBoletas > 1 ? 's' : ''}
                      </span>
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400">
                        Activo
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                      ${formatearMonto(planActivo.montoCuota)}<span className="text-sm font-normal text-gray-400 ml-1">/mes</span>
                    </p>
                    <button
                      onClick={() => setShowDetallePlan(true)}
                      className="w-full border border-indigo-300 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400 text-sm font-semibold py-2.5 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                    >
                      Ver detalles
                    </button>
                  </div>
                ) : boletasAdeudadas.length > 0 ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-[#617c89] dark:text-gray-400">
                        {boletasAdeudadas.length} boleta{boletasAdeudadas.length > 1 ? 's' : ''} financiable{boletasAdeudadas.length > 1 ? 's' : ''}
                      </p>
                      <p className="text-2xl font-bold text-[#111618] dark:text-white mt-1">
                        ${formatearMonto(totalDeudaConsolidado)}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">Hasta {planConfig.maxCuotas} cuotas sin interés</p>
                    </div>
                    <button
                      onClick={abrirPlanPagoConsolidado}
                      className="w-full bg-indigo-600 text-white text-sm font-semibold py-2.5 rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      Crear plan de pago
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 py-1">
                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#111618] dark:text-white">Sin deuda</p>
                      <p className="text-xs text-gray-400">No necesitás financiamiento</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Data Table */}
        <div>
          <h2 className="text-base font-semibold text-[#111618] dark:text-white mb-4">
            Estado de cuenta
          </h2>
          {boletas.length === 0 ? (
            <div className="p-8 text-center text-[#617c89] dark:text-gray-400 bg-white dark:bg-[#101c22] rounded-xl border border-[#dbe2e6] dark:border-gray-700">
              No hay boletas registradas
            </div>
          ) : (
            <>
              {/* Vista Mobile: tarjetas */}
              <div className="flex flex-col gap-3 md:hidden">
                {boletas.map((boleta) => (
                  <div
                    key={boleta.id}
                    className={`rounded-xl border bg-white dark:bg-[#101c22] p-4 ${
                      boleta.estado === 'vencida'
                        ? 'border-red-200 dark:border-red-900/40'
                        : boleta.estado === 'pendiente'
                          ? 'border-amber-200 dark:border-amber-900/40'
                          : 'border-[#dbe2e6] dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <p className="text-base font-bold text-[#111618] dark:text-white">
                        {formatearPeriodo(boleta.mes, boleta.anio)}
                      </p>
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${estadoColor(boleta.estado)}`}>
                        {estadoTexto(boleta.estado)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm mb-4">
                      <div>
                        <p className="text-xs text-[#617c89] dark:text-gray-400">Vence</p>
                        <p className="font-medium text-[#111618] dark:text-white tabular-nums">
                          {formatApiDate(boleta.fechaVencimiento)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-[#617c89] dark:text-gray-400">Monto</p>
                        <p className="font-bold text-lg text-[#111618] dark:text-white tabular-nums">
                          ${parseFloat(boleta.total).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {boleta.estado === 'pendiente' && (
                        <button
                          onClick={() => navigate(`/cliente/pagar-boleta/${boleta.id}`)}
                          className="flex-1 rounded-lg bg-[#007BFF] text-white text-sm font-semibold py-2.5 hover:bg-[#007BFF]/90 transition-colors"
                        >
                          Pagar
                        </button>
                      )}
                      <button
                        onClick={() => handleVerDetalle(boleta)}
                        className="flex-1 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-sm font-semibold py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        {boleta.estado === 'procesando'
                          ? 'Ver Estado'
                          : boleta.estado === 'plan_pago'
                            ? 'Ver Plan'
                            : 'Detalle'}
                      </button>
                      <button
                        onClick={() => handleDescargarPdf(boleta)}
                        disabled={descargandoId === boleta.id}
                        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-600 dark:text-gray-300 text-sm font-semibold px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {descargandoId === boleta.id ? '...' : 'PDF'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Vista Desktop: tabla */}
              <div className="hidden md:block overflow-x-auto bg-white dark:bg-[#101c22] rounded-xl border border-[#dbe2e6] dark:border-gray-700">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-[#dbe2e6] dark:border-gray-700 text-xs text-[#617c89] dark:text-gray-400 uppercase tracking-wide">
                    <tr>
                      <th scope="col" className="px-6 py-3.5">Período</th>
                      <th scope="col" className="px-6 py-3.5">Vencimiento</th>
                      <th scope="col" className="px-6 py-3.5">Monto</th>
                      <th scope="col" className="px-6 py-3.5">Estado</th>
                      <th scope="col" className="px-6 py-3.5 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {boletas.map((boleta) => (
                      <tr
                        key={boleta.id}
                        className="hover:bg-gray-50/60 dark:hover:bg-white/[0.02] transition-colors"
                      >
                        <td className="px-6 py-4 font-semibold text-[#111618] dark:text-white whitespace-nowrap">
                          {formatearPeriodo(boleta.mes, boleta.anio)}
                        </td>
                        <td className="px-6 py-4 text-[#617c89] dark:text-gray-400 tabular-nums">
                          {formatApiDate(boleta.fechaVencimiento)}
                        </td>
                        <td className="px-6 py-4 font-semibold text-[#111618] dark:text-gray-200 tabular-nums">
                          ${parseFloat(boleta.total).toFixed(2)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${estadoColor(boleta.estado)}`}>
                            {estadoTexto(boleta.estado)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {boleta.estado === 'pendiente' && (
                              <button
                                onClick={() => navigate(`/cliente/pagar-boleta/${boleta.id}`)}
                                className="px-3 py-1.5 rounded-lg bg-[#007BFF] text-white text-xs font-semibold hover:bg-[#007BFF]/90 transition-colors shadow-sm"
                              >
                                Pagar
                              </button>
                            )}
                            <button
                              onClick={() => handleVerDetalle(boleta)}
                              className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-xs font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                            >
                              {boleta.estado === 'procesando'
                                ? 'Ver Estado'
                                : boleta.estado === 'plan_pago'
                                  ? 'Ver Plan'
                                  : 'Ver Detalle'}
                            </button>
                            <button
                              onClick={() => handleDescargarPdf(boleta)}
                              disabled={descargandoId === boleta.id}
                              title="Descargar PDF"
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-600 dark:text-gray-300 text-xs font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <span className={`material-symbols-outlined text-base ${descargandoId === boleta.id ? 'animate-spin' : ''}`}>
                                {descargandoId === boleta.id ? 'progress_activity' : 'picture_as_pdf'}
                              </span>
                              <span className="text-xs font-medium">PDF</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </main >
    </div >
  );
}

export default DashboardCliente;
